import { defineComponent, Types } from 'bitecs';
// ============================================================================
// Core Components
// ============================================================================
// Position component - world coordinates
export const Position = defineComponent({
    x: Types.f32,
    y: Types.f32,
});
// Velocity component - movement per tick
export const Velocity = defineComponent({
    x: Types.f32,
    y: Types.f32,
});
// Rotation component - facing direction in radians
export const Rotation = defineComponent({
    angle: Types.f32,
});
// Health component
export const Health = defineComponent({
    current: Types.f32,
    max: Types.f32,
});
// ============================================================================
// Entity Identity Components
// ============================================================================
// NetworkEntity - for replication
export const NetworkEntity = defineComponent({
    networkId: Types.ui32,
    lastUpdateTick: Types.ui32,
});
// Chunk presence - which chunk the entity is in
export const ChunkPosition = defineComponent({
    x: Types.i32,
    y: Types.i32,
});
// ============================================================================
// Pawn Components
// ============================================================================
// Pawn component - marks entity as a controllable pawn
export const Pawn = defineComponent({
    ownerId: Types.ui32, // network ID of owning player
    state: Types.ui8, // PawnState enum
    isDowned: Types.ui8, // 1 if pawn is downed (0 HP but not dead)
    isUnconscious: Types.ui8, // 1 if unconscious (cannot act, must be carried)
    downedAtTick: Types.ui32, // tick when pawn was downed (for crawl timer)
    carriedByEntityId: Types.ui32, // network ID of pawn carrying this one (0 = not carried)
    carryingEntityId: Types.ui32, // network ID of pawn being carried (0 = not carrying)
    restingInBedNetworkId: Types.ui32, // network ID of bed if resting/healing (0 = not in bed)
});
// Needs - pawn needs (hunger, rest, etc.)
export const Needs = defineComponent({
    hunger: Types.f32,
    rest: Types.f32,
    mood: Types.f32,
});
// PathFollower - entity is following a path
// Note: Full path waypoints stored externally in PathStore (variable length)
export const PathFollower = defineComponent({
    active: Types.ui8, // 1 if following path, 0 if not
    currentIndex: Types.ui16, // current waypoint index in path
    targetX: Types.f32, // current waypoint X
    targetY: Types.f32, // current waypoint Y
    finalX: Types.f32, // final destination X
    finalY: Types.f32, // final destination Y
    pathLength: Types.ui16, // total waypoints in path
});
// Task - current task being performed
export const Task = defineComponent({
    active: Types.ui8, // 1 if has task, 0 if not
    taskType: Types.ui8, // TaskType enum
    targetX: Types.f32,
    targetY: Types.f32,
    targetEntity: Types.ui32, // network ID of target
    progress: Types.f32, // 0-1 completion
    workStartTime: Types.f32, // performance.now() when work started (0 if not started)
    workDuration: Types.f32, // milliseconds to complete
});
// PawnAI - autonomous AI behavior tracking
export const PawnAI = defineComponent({
    currentBehavior: Types.ui8, // PawnAIBehavior enum
    lastDecisionTick: Types.ui32, // When AI last made a decision
    lastWanderTick: Types.ui32, // When last wander destination was picked
    lastSocialTick: Types.ui32, // When last socialized
    socialTargetId: Types.ui32, // Network ID of social interaction target
    assignedBedId: Types.ui32, // Network ID of assigned bed (0 if none)
    forceSleep: Types.ui8, // 1 if pawn collapsed from exhaustion and cannot be commanded
    isAutonomousTask: Types.ui8, // 1 if current task was set by AI (interruptible)
    homeX: Types.f32, // Home position for wandering
    homeY: Types.f32,
    wanderRadius: Types.f32, // How far from home to wander
});
// ============================================================================
// Player Components
// ============================================================================
// Player component - marks entity as a connected player
export const Player = defineComponent({
    connectionId: Types.ui32,
    lastInputTick: Types.ui32,
});
// ============================================================================
// Rendering Components
// ============================================================================
// Sprite reference - for rendering
export const Sprite = defineComponent({
    spriteId: Types.ui16,
    width: Types.ui8,
    height: Types.ui8,
});
// Selectable - can be selected by player
export const Selectable = defineComponent({
    selected: Types.ui8, // boolean as u8
});
// ============================================================================
// World Object Components
// ============================================================================
// WorldObject component - objects placed on tiles (trees, walls, rocks, furniture)
export const WorldObject = defineComponent({
    objectType: Types.ui16, // ObjectTypeValue
    tileX: Types.i32, // World tile X coordinate
    tileY: Types.i32, // World tile Y coordinate
    rotation: Types.ui8, // 0-3 for 90 degree rotations
    state: Types.ui8, // Object-specific state (open/closed, on/off, etc.)
    ownerId: Types.ui32, // Player who placed it (0 for natural objects)
    constructionProgress: Types.f32, // 0-1 for buildings under construction
});
// Building component
export const Building = defineComponent({
    buildingType: Types.ui16,
    constructionProgress: Types.f32,
    isConstructed: Types.ui8,
});
// Resource component (trees, ores, etc.)
export const Resource = defineComponent({
    resourceType: Types.ui16,
    amount: Types.f32,
});
// ============================================================================
// Inventory Components
// ============================================================================
// Inventory - simplified inventory
export const Inventory = defineComponent({
    capacity: Types.ui8,
    usedSlots: Types.ui8,
});
// ============================================================================
// Construction Components
// ============================================================================
// ConstructionSite - marks an entity as a construction in progress
export const ConstructionSite = defineComponent({
    objectType: Types.ui16, // ObjectTypeValue to build
    tileX: Types.i32, // World tile X coordinate
    tileY: Types.i32, // World tile Y coordinate
    width: Types.ui8, // Width in tiles
    height: Types.ui8, // Height in tiles
    ownerId: Types.ui32, // Player who ordered construction
    progress: Types.f32, // 0-1 construction progress
    state: Types.ui8, // ConstructionStateValue
    workersAssigned: Types.ui8, // Number of pawns currently working
    resourcesDelivered: Types.ui8, // 1 if resources delivered, 0 if not
    baseWorkDuration: Types.f32, // Base work duration in ms
    accumulatedWork: Types.f32, // Accumulated work in ms
});
// ============================================================================
// Animal Components
// ============================================================================
// Animal - marks entity as a wildlife animal
export const Animal = defineComponent({
    npcType: Types.ui16, // NpcTypeValue
    aiState: Types.ui8, // NpcAIStateValue
    packId: Types.ui32, // 0 if no pack
    targetEntityId: Types.ui32, // For hunting/fleeing
    homeX: Types.f32, // Territory center
    homeY: Types.f32,
    territoryRadius: Types.f32,
    lastSeenThreatTick: Types.ui32,
    lastAteAtTick: Types.ui32,
    lastSleptAtTick: Types.ui32,
    sleepStartTick: Types.ui32, // 0 if not sleeping
    attackCooldownUntil: Types.ui32, // Server tick when can attack again
    isLeader: Types.ui8, // 1 if pack leader
});
// NpcNeeds - biological needs for animals
export const NpcNeeds = defineComponent({
    hunger: Types.f32, // 0-1, 1 = starving
    tiredness: Types.f32, // 0-1, 1 = exhausted
    fear: Types.f32, // 0-1, affects fleeing behavior
});
// PackMember - pack management for social animals
// Note: Member list stored externally in PackStore
export const PackMember = defineComponent({
    packId: Types.ui32,
    leaderEntityId: Types.ui32,
    memberCount: Types.ui8,
    isHunting: Types.ui8,
    targetEntityId: Types.ui32,
    centerX: Types.f32,
    centerY: Types.f32,
});
// ============================================================================
// Detailed Health Components
// ============================================================================
// DetailedHealth - detailed health system for pawns and animals
// Note: Body part states and conditions stored externally in HealthStore
export const DetailedHealth = defineComponent({
    overallHealth: Types.f32, // 0-100 calculated
    consciousness: Types.f32, // 0-1, below 0.3 = incapacitated
    bloodLevel: Types.f32, // 0-1, blood remaining
    bodyTemperature: Types.f32, // Celsius
    comfortableMinTemp: Types.f32,
    comfortableMaxTemp: Types.f32,
    pain: Types.f32, // 0-1
    bleedRate: Types.f32, // Blood loss per second (summed from conditions)
    isDead: Types.ui8, // 1 if dead
    isIncapacitated: Types.ui8, // 1 if downed
});
// Mood - mental state for humanoids (optional for animals)
export const Mood = defineComponent({
    current: Types.f32, // 0-100
    target: Types.f32, // Mood trending towards
    mentalBreakRisk: Types.f32, // 0-1, chance of mental break
});
// Temperature - environmental temperature at entity location
export const Temperature = defineComponent({
    ambient: Types.f32, // Celsius, surrounding temperature
    felt: Types.f32, // Celsius, after insulation
});
// ============================================================================
// Combat Components
// ============================================================================
// Combatant - entity can engage in combat
export const Combatant = defineComponent({
    combatPower: Types.f32, // Overall combat strength
    armorRating: Types.f32, // 0-1 damage reduction
    meleeSkill: Types.f32, // 0-20 melee combat skill
    rangedSkill: Types.f32, // 0-20 ranged combat skill
    dodgeChance: Types.f32, // 0-1 chance to dodge
    lastAttackTick: Types.ui32, // When last attacked
    targetEntityId: Types.ui32, // Current combat target
    isFleeing: Types.ui8, // 1 if fleeing from combat
    isAggressive: Types.ui8, // 1 if actively seeking combat
});
// Attack - attack being performed
export const Attack = defineComponent({
    attackType: Types.ui8, // AttackTypeValue
    damage: Types.f32,
    damageType: Types.ui8, // DamageTypeValue
    cooldownMs: Types.f32,
    range: Types.f32, // Tiles
    accuracy: Types.f32, // 0-1
    inProgress: Types.ui8, // 1 if currently attacking
    startTick: Types.ui32,
});
// Corpse - dead entity that retains identity and can be interacted with
export const Corpse = defineComponent({
    // Identity
    originalEntityType: Types.ui8, // EntityType.PAWN or ANIMAL
    originalSubType: Types.ui16, // NpcTypeValue (0 for pawns)
    originalOwnerId: Types.ui32, // Player who owned (0 for wild)
    originalMaxHealth: Types.f32, // For resource scaling
    // Death context
    deathTick: Types.ui32, // Server tick when died
    killerNetworkId: Types.ui32, // 0 if natural/starvation
    causeOfDeath: Types.ui8, // CauseOfDeathValue enum
    // Resources (animals only, 0 for pawns)
    meatYield: Types.f32, // Base meat available
    leatherYield: Types.f32, // Base leather available
    // Decay state
    rotProgress: Types.f32, // 0-1 decay progress
    rotStage: Types.ui8, // RotStageValue enum
    isFrozen: Types.ui8, // 1 if in freezer (stops decay)
    // Hauling state
    isReserved: Types.ui8, // 1 if reserved for task
    reservedByNetworkId: Types.ui32, // Network ID of pawn with reservation
});
// ============================================================================
// Combat Mode Component
// ============================================================================
// CombatModeState - tracks combat mode and current target for pawns
export const CombatModeState = defineComponent({
    mode: Types.ui8, // CombatModeValue (passive, defensive, aggressive, hunt)
    targetNetworkId: Types.ui32, // Current combat target (0 if none)
    targetType: Types.ui8, // TargetTypeValue (pawn, animal, etc.)
    combatState: Types.ui8, // CombatStateValue (none, approaching, attacking, etc.)
    lastAttackTick: Types.ui32, // Last attack tick
    aggroStartTick: Types.ui32, // When current aggro started
    lastDamagedByTick: Types.ui32, // When last took damage
    lastDamagedByEntityId: Types.ui32, // Who last damaged this entity
});
// ============================================================================
// Projectile Components
// ============================================================================
// Projectile - flying entity that travels to target
export const Projectile = defineComponent({
    projectileType: Types.ui8, // ProjectileTypeValue
    sourceEntityId: Types.ui32, // Network ID of entity that fired
    targetX: Types.f32, // Target position X
    targetY: Types.f32, // Target position Y
    targetEntityId: Types.ui32, // Target entity network ID (0 = ground target)
    speed: Types.f32, // Tiles per second
    damage: Types.f32, // Pre-calculated damage
    accuracy: Types.f32, // Hit chance (0-1)
    startX: Types.f32, // Start position X
    startY: Types.f32, // Start position Y
    spawnTick: Types.ui32, // Server tick when spawned
});
//# sourceMappingURL=components.js.map